/**
 * Accept invite page — join a household via invite link.
 */

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useToast } from '../store/ToastContext';
import { acceptInvite } from '../api/household';
import { Spinner } from '../components/Spinner';

export function AcceptInvitePage() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    if (!code) {
      setStatus('error');
      setErrorMessage('Invalid invite link');
      return;
    }

    handleAcceptInvite(code);
  }, [code]);

  async function handleAcceptInvite(inviteCode: string) {
    setStatus('loading');
    try {
      const household = await acceptInvite(inviteCode);
      setStatus('success');
      addToast('success', `Joined ${household.name}!`);
      // Wait a moment to show success, then redirect
      setTimeout(() => {
        navigate('/boxes');
      }, 1500);
    } catch (err) {
      setStatus('error');
      const errorMsg = err instanceof Error ? err.message : 'Failed to accept invite';
      setErrorMessage(errorMsg);
      addToast('error', errorMsg);
    }
  }

  if (status === 'loading') {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-4">
        <Spinner />
        <p className="text-sm text-text-secondary">Joining household...</p>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-4">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-500/10">
          <svg
            className="h-8 w-8 text-green-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <div className="text-center space-y-2">
          <h1 className="text-xl font-bold text-text-primary">Success!</h1>
          <p className="text-sm text-text-secondary">
            You&apos;ve joined the household. Redirecting...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-4 max-w-md mx-auto px-4">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-danger/10">
        <svg
          className="h-8 w-8 text-danger"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
      </div>
      <div className="text-center space-y-2">
        <h1 className="text-xl font-bold text-text-primary">Unable to Join</h1>
        <p className="text-sm text-text-secondary">{errorMessage}</p>
      </div>
      <button
        type="button"
        onClick={() => navigate('/boxes')}
        className="rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-white hover:bg-accent-hover transition-colors"
      >
        Go to Boxes
      </button>
    </div>
  );
}
